// src/auth/route-guard.tsx
//
// Client-side gatekeeper for any route that should only be reachable by
// an authenticated Forge Ultra user.
//
// A user may enter the internal dashboard if AND ONLY IF the backend's
// ``GET /users/me`` confirms ONE of:
//
//   * roles includes "administrator"            (WordPress site staff)
//   * membership_level === "Ki-Wealth Premium"  (PMPro level 3)
//   * membership_level === "Ki-Wealth Professional" (PMPro level 4)
//
// Anyone else is handled as follows:
//
//   * No / invalid / expired token         → render an explanatory
//     "You are not authorized" screen with a manual "Sign In at
//     Ki-Wealth" button that sends the visitor to the Ki-Wealth
//     homepage. We do NOT auto-bounce: a deliberate click avoids
//     surprising redirects for visitors who landed on /dashboard
//     directly (bookmarks, shared links, etc.).
//   * Logged in but unauthorised level     → https://www.ki-wealth.com/membership-levels
//
// We deliberately reuse the shared `apiClient` (rather than `fetch` with
// `credentials: 'include'`) because Forge Ultra's auth model is a Bearer
// JWT in localStorage — there is no cookie session. The axios request
// interceptor in `api/client.ts` attaches the token; the response
// interceptor handles the 401 → WP-login bounce centrally.

import { AxiosError } from 'axios';
import { useEffect, useState, type ReactNode } from 'react';

import {
    clearLoginBounce,
    clearToken,
    isLoginBounceLoop,
} from '../api/auth-storage';
import { fetchCurrentUser, type UserProfile } from '../api/users';

// External pages we redirect to when authorisation fails. Configurable
// per-env so a staging WP can override them without rebuilding.
//
// VITE_WP_LOGIN_URL points at the SSO bridge query handled by the
// ki-wealth-auth-bridge.php WP plugin: when an already-authenticated WP
// user lands there, the plugin mints a fresh JWT and 302s the browser
// back to /login-bridge?token=<JWT> on the SPA — completing the
// handshake transparently without a second login prompt.
const WP_LOGIN_URL: string =
    import.meta.env.VITE_WP_LOGIN_URL ??
    'https://www.ki-wealth.com/sso-bridge';
const WP_MEMBERSHIP_URL: string =
    import.meta.env.VITE_WP_MEMBERSHIP_URL ??
    'https://www.ki-wealth.com/membership-levels';
// Public Ki-Wealth homepage — the destination shown to anonymous
// visitors who land on /dashboard without a session. Configurable so a
// staging WP instance can override without rebuilding the SPA.
const KI_WEALTH_HOME_URL: string =
    import.meta.env.VITE_KI_WEALTH_HOME_URL ?? 'https://www.ki-wealth.com/';

// Membership levels that may enter the internal dashboard. Mirrors the
// backend's `ALLOWED_MEMBERSHIP_LEVELS` env var.
export const ALLOWED_MEMBERSHIP_LEVELS: readonly string[] = [
    'Ki-Wealth Premium',
    'Ki-Wealth Professional',
];

// WordPress role slugs that bypass the membership gate.
export const PRIVILEGED_ROLES: readonly string[] = ['administrator'];

export function isAuthorisedUser(user: Pick<UserProfile, 'roles' | 'membership_level'>): boolean {
    const roles = user.roles ?? [];
    if (roles.some((role) => PRIVILEGED_ROLES.includes(role))) return true;
    return Boolean(
        user.membership_level &&
            ALLOWED_MEMBERSHIP_LEVELS.includes(user.membership_level),
    );
}

// ``login-required`` is the dead-end state we enter when the SPA detects
// it is inside a redirect loop with WordPress (see bounceToLoginOnce in
// auth-storage.ts). Instead of bouncing again we render a manual
// sign-in CTA so the user can break out of the loop with one click.
type GuardStatus =
    | 'checking'
    | 'authorised'
    | 'unauthorised'
    | 'unauthenticated'
    | 'login-required';

interface ProtectedRouteProps {
    children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
    const [status, setStatus] = useState<GuardStatus>('checking');

    useEffect(() => {
        let cancelled = false;

        // eslint-disable-next-line no-console
        console.info('[ProtectedRoute] verifying Forge Ultra session via /users/me');

        // ``skipAuthBounce`` tells the axios response interceptor NOT
        // to redirect us off-site on a 401 — this component owns the
        // "no session" UX (the "You are not authorized" screen below)
        // and bouncing would prevent that screen from ever rendering.
        fetchCurrentUser({ skipAuthBounce: true })
            .then((user) => {
                if (cancelled) return;
                // Successful auth — clear the loop-protection marker so
                // the next legitimate session-expiry can still bounce.
                clearLoginBounce();
                // eslint-disable-next-line no-console
                console.info('[ProtectedRoute] authenticated user', {
                    email: user.email,
                    membership_level: user.membership_level,
                    roles: user.roles,
                });
                setStatus(isAuthorisedUser(user) ? 'authorised' : 'unauthorised');
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                // Treat anything non-2xx as "no valid session". The axios
                // 401 interceptor in api/client.ts may already be
                // navigating; we still set state so React stops rendering
                // children if the redirect is slow.
                const status =
                    err instanceof AxiosError ? err.response?.status : undefined;
                // eslint-disable-next-line no-console
                console.warn('[ProtectedRoute] /users/me rejected', {
                    status,
                    inLoop: isLoginBounceLoop(),
                });
                if (status === 403) {
                    setStatus('unauthorised');
                } else if (isLoginBounceLoop()) {
                    // We just bounced to WP login and came back without a
                    // valid session — WordPress is not seeding a fresh
                    // JWT for this already-authenticated visitor. Stop
                    // bouncing and surface a manual sign-in CTA.
                    setStatus('login-required');
                } else {
                    setStatus('unauthenticated');
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (status === 'unauthorised') {
            window.location.href = WP_MEMBERSHIP_URL;
        }
        // 'unauthenticated' is intentionally NOT auto-redirected: we
        // render an explanatory screen with a manual sign-in button
        // (see render branch below) so anonymous visitors who land on
        // /dashboard directly understand why they were stopped.
    }, [status]);

    if (status === 'authorised') return <>{children}</>;

    if (status === 'checking') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
                <p className="text-lg font-medium">Loading Platform…</p>
            </div>
        );
    }

    if (status === 'unauthenticated') {
        // Anonymous visitor (or expired token) hit a protected route.
        // Show the access notice and a manual CTA that sends them to
        // the Ki-Wealth homepage to register / log in.
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-6 text-center">
                <p className="max-w-xl text-base sm:text-lg">
                    You are not authorized to visit Forge Ultra Proprietary
                    Platform. You must be Ki-Wealth&apos;s Premium or
                    Professional member. Go to Ki-Wealth, register or log in.
                </p>
                <a
                    href={KI_WEALTH_HOME_URL}
                    onClick={(event) => {
                        // Drive the navigation explicitly so we can
                        // wipe any stale token / bounce marker first —
                        // otherwise a returning visitor with an expired
                        // JWT could re-enter the protected route in a
                        // half-authenticated state.
                        event.preventDefault();
                        clearLoginBounce();
                        clearToken();
                        // eslint-disable-next-line no-console
                        console.info(
                            '[ProtectedRoute] Sign In at Ki-Wealth clicked; redirecting to',
                            KI_WEALTH_HOME_URL,
                        );
                        window.location.assign(KI_WEALTH_HOME_URL);
                    }}
                    className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium shadow"
                >
                    Sign In at Ki-Wealth
                </a>
            </div>
        );
    }

    if (status === 'login-required') {
        // Loop-break UI: the user is logged into WordPress but the SPA
        // never received a JWT (WP did not seed one for this session).
        // A manual click clears the bounce marker and re-attempts the
        // login flow, this time as an explicit user action.
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-6 text-center">
                <h1 className="text-2xl font-semibold">Sign in to access Forge Ultra</h1>
                <p className="max-w-md text-base">
                    Your Ki-Wealth session did not establish a Forge Ultra token.
                    Please sign in again to continue.
                </p>
                <a
                    href={WP_LOGIN_URL}
                    onClick={(event) => {
                        // The native <a> navigation alone is not always
                        // enough: if the apiClient's response interceptor
                        // wrote a fresh bounce timestamp during the
                        // in-flight 401 that produced this screen, the
                        // very next /users/me on the way back will
                        // re-trigger ``isLoginBounceLoop()`` and dump the
                        // user right back here — making the button
                        // appear to "do nothing".
                        //
                        // Force a fully-clean state on click and drive
                        // the navigation explicitly so we know exactly
                        // what is happening (and it shows up in the
                        // browser console for the user's own log capture).
                        event.preventDefault();
                        clearLoginBounce();
                        clearToken();
                        // eslint-disable-next-line no-console
                        console.info(
                            '[ProtectedRoute] manual sign-in clicked; redirecting to',
                            WP_LOGIN_URL,
                        );
                        window.location.assign(WP_LOGIN_URL);
                    }}
                    className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium shadow"
                >
                    Sign in at Ki-Wealth
                </a>
            </div>
        );
    }

    // Redirect is already in flight (see the second useEffect). Render
    // nothing rather than flashing the protected children.
    return null;
}
